// @flow
import React, { type Node, Component, Fragment } from 'react';
import { get, noop, memoize } from 'lodash';
import { compose } from 'redux';
import dynamic from 'next/dynamic';

import { MAX_NUMBER_OF_SELECTED_PRODUCTS } from 'Utils/faceting/constants';
import {
  getProductProps,
  setProductHref,
  updateProduct,
  updateProductUrl,
} from 'Components/widgets/product/Product.mapper';

import {
  ProductsGrid,
  type Props as ProductsGridProps,
} from 'Components/widgets/products-grid/ProductsGrid';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { SearchBanners } from 'Components/widgets/facet-search-results/search-banners/SearchBanners';
import { SearchCompare } from 'Components/widgets/facet-search-results/search-compare/SearchCompare';
import type { ProductProps } from 'Components/widgets/product/Product.utils';

const ProductsGridVirtualized = dynamic(
  () =>
    import('Components/widgets/products-grid-virtualized/ProductsGridVirtualized').then(
      c => c.ProductsGridVirtualized,
    ),
  { ssr: false },
);

export type Props = {
  childcareAvailabilities: Array<*>,
  className?: string,
  comparisonUrl: string,
  title: Node,
  config: *,
  offer?: Offer,
  resorts: Array<*>,
  isProductComparisonModeAuthorized?: boolean,
  isProductComparisonSelectionModeEnabled: boolean,
  isProductsBottomInViewport?: boolean,
  isProductsTopInViewport?: boolean,
  onClickCompareButton: () => void,
  toggleProductComparisonSelectionMode: () => void,
  hasDepartureCity?: boolean,
  hasDepartureDate?: boolean,
  productsSelectedForComparison: Array<string>,
  maxSelectableItems: number,
  toggleProductSelectedForComparison?: string => mixed,
  displayValidationButton: boolean,
  filteredProductsId: Array<string>,
  isPriceLoading: boolean,
  prices: Array<*>,
  hasSeeMore?: boolean,
  seeMore?: $PropertyType<ProductsGridProps, 'seeMore'>,
  virtualizedGrid?: boolean,
  isSynchronousGrid?: boolean,
};

const defaultProps = {
  config: {},
  resorts: [],
  displayValidationButton: true,
  isProductComparisonSelectionModeEnabled: false,
  productsSelectedForComparison: [],
  maxSelectableItems: 3,
  filteredProductsId: [],
  toggleProductSelectedForComparison: noop,
  isPriceLoading: false,
  prices: [],
  childcareAvailabilities: [],
  isSynchronousGrid: false,
};

export class SearchResults extends Component<Props> {
  static defaultProps = defaultProps;

  flattenResort = (product: *) => {
    const {
      productsSelectedForComparison,
      maxSelectableItems,
      isProductComparisonSelectionModeEnabled,
      displayValidationButton,
      config,
      isPriceLoading,
    } = this.props;
    const productId = product.id;
    const isSelectedForComparison = productsSelectedForComparison.includes(productId);
    const maxSelectableItemsReached = productsSelectedForComparison.length === maxSelectableItems;

    return {
      hasComparison: isProductComparisonSelectionModeEnabled,
      isSliderDisabled: isProductComparisonSelectionModeEnabled,
      comparison: {
        isDisabled:
          isProductComparisonSelectionModeEnabled &&
          !isSelectedForComparison &&
          maxSelectableItemsReached,
        isSelectable:
          isProductComparisonSelectionModeEnabled &&
          (isSelectedForComparison || !maxSelectableItemsReached),
        isSelected: isSelectedForComparison,
      },
      displayValidationButton,
      config,
      offerColor: get(this.props, ['offer', 'color']),
      isPriceLoading,
      product: {
        ...product,
        price: this.getPriceForProduct(productId),
        childcareAvailabilities: this.getChildcareAvailabilitiesForProduct(productId),
      },
    };
  };

  getPriceForProduct(productId: string) {
    const product = this.props.prices.find(p => p.id === productId);
    return get(product, 'price', {});
  }

  getChildcareAvailabilitiesForProduct(productId: string): Array<*> {
    return this.props.childcareAvailabilities.filter(
      childcareAvailability => childcareAvailability.productId === productId,
    );
  }

  getProducts = memoize(
    ({ products }: { products: Array<*> }): Array<ProductProps> => {
      return products.map(product =>
        compose(
          updateProductUrl,
          getProductProps,
          setProductHref,
          this.flattenResort,
        )(product),
      );
    },
  );

  /**
   * Mutate the products to set the price or the comparison mode
   * We mutate the product to re-render only the subcomponents which needed it
   */
  getProductWithModifier() {
    return this.getProducts({ products: this.props.resorts, prices: this.props.prices }).map<
      ProductProps,
    >((product, index) => updateProduct(product, this.flattenResort(this.props.resorts[index])));
  }

  withCallToAction(): boolean {
    const { thumbnailCTA, thumbnailTourCTA } = get(this.props, 'config.common', {});
    const seeMoreText = !!this.props.config?.common?.editorial?.search?.seeMoreText;

    return Boolean(thumbnailCTA || thumbnailTourCTA || seeMoreText);
  }

  render() {
    const {
      comparisonUrl,
      config,
      isProductComparisonModeAuthorized,
      isProductComparisonSelectionModeEnabled,
      isProductsBottomInViewport,
      isProductsTopInViewport,
      onClickCompareButton,
      productsSelectedForComparison,
      toggleProductComparisonSelectionMode,
      toggleProductSelectedForComparison,
      hasDepartureDate,
      hasDepartureCity,
      offer,
      isSynchronousGrid,
    } = this.props;

    const display = this.props.hasSeeMore
      ? {
          mobile: 2,
          phablet: 4,
          tablet: 6,
          desktop: 6,
        }
      : undefined;

    const products =
      this.props.prices.length === 0 && this.props.childcareAvailabilities.length === 0
        ? this.getProducts({ products: this.props.resorts, prices: this.props.prices })
        : this.getProductWithModifier();

    const hasChildcareIncluded = this.props.childcareAvailabilities.length > 0;

    return (
      <Fragment>
        {isProductComparisonModeAuthorized ? (
          <SearchCompare
            className="mb-6"
            buttonLabel={get(config, 'faceting.compare.buttonLabel')}
            cancelLabel={get(config, 'faceting.compare.cancelLabel')}
            comparisonToggle={toggleProductComparisonSelectionMode}
            comparisonClick={onClickCompareButton}
            comparisonUrl={comparisonUrl}
            collapseLabel={get(config, 'faceting.compare.collapseLabel')}
            dropzoneLabel={get(config, 'faceting.compare.dropzoneLabel')}
            expandLabel={get(config, 'faceting.compare.expandLabel')}
            isComparisonActive={isProductComparisonSelectionModeEnabled}
            isComparisonSubmitable={
              productsSelectedForComparison.length > 0 && hasDepartureDate && hasDepartureCity
            }
            isProductsBottomInViewport={isProductsBottomInViewport}
            isProductsTopInViewport={isProductsTopInViewport}
            mandatoryMessage={
              !hasDepartureDate || !hasDepartureCity
                ? get(config, 'faceting.compare.dateAndTransportMandatory')
                : ''
            }
            resultsCount={products.length}
            selectedItems={products
              .filter(product => productsSelectedForComparison.includes(product.id))
              .map(product => ({
                header: product.header,
                id: product.id,
                order: productsSelectedForComparison.findIndex(p => p === product.id),
                picture: get(product, 'thumbnail.pictures[0]'),
                price: get(product, 'flag.price'),
                pricingTable: product?.flag?.pricingTable,
                url: product.url,
                offer,
              }))
              .sort((a, b) => a.order - b.order)}
            selectedItemsMax={MAX_NUMBER_OF_SELECTED_PRODUCTS}
            toggleProductSelectedForComparison={toggleProductSelectedForComparison}
          >
            {this.props.title}
          </SearchCompare>
        ) : (
          <SiteContainer>{this.props.title}</SiteContainer>
        )}

        <SearchBanners position="top" />

        <SiteContainer>
          {this.props.virtualizedGrid && !hasChildcareIncluded && !this.withCallToAction() ? (
            <ProductsGridVirtualized
              className={this.props.className}
              products={products}
              onComparisonClick={toggleProductSelectedForComparison}
            />
          ) : (
            <ProductsGrid
              className={this.props.className}
              products={products}
              totalNumberToDisplay={this.props.resorts.length}
              seeMore={this.props.seeMore}
              onComparisonClick={toggleProductSelectedForComparison}
              hasSeeMore={this.props.hasSeeMore}
              display={display}
              offer={offer}
              isSynchronousGrid={isSynchronousGrid}
            />
          )}
          <div id="SearchCompareBeacon" />
        </SiteContainer>

        <SearchBanners position="terms" />
      </Fragment>
    );
  }
}
